
import { BroadcastChannel } from 'broadcast-channel';
import nsEvents from '@nuskin/events';
import * as _ from 'lodash';
import RunConfigUtil from './runConfigUtil.js';

/**
 *
 * For centralizing and standardizing the use of JavaScript events across the nuskin site.
 *
 *
 * IMPLEMENTING
 * Implementing events via nuskin.events is simple and straightforward.
 *
 * (1) Add the event id String as a variable in the nuskin.events.js file, in the appropriate location, such as: MY_NEW_ERROR: "myNewError".
 * (2) Publish the event through the appropriate nuskin.events wrapper function, such as nuskin.events.publishError(nuskin.events.MY_NEW_ERROR);
 * (3) Subscribe from the code location where you'd like to handle the event: nuskin.events.subscribe( nuskin.events.MY_NEW_ERROR, function(evt) {//handle event});
 *
 * Note that as of now, the wrapper functions for subscribe and publish don't do anything other than route to nsEvents.publish()
 * and nsEvents.subscribe(). But if we use the wrapper functions here we can centralize for adaptability later on.
 *
 * Also note that the functions for error event handling are designed to help standardize the transmission of data in
 * cases such as AJAX request errors.  One could just use the jQuery pub/sub functions directly, but then if we need to
 * change the event handling down the line, we'd need to handle those special cases separately.
 *
 * MESSAGE ID
 * The 'messageId' functionality,  is designed to work with the localized common strings available through the
 * StringsService global object. The 'messageId' parameter on the 'eventObject' serves to pass the value of the string id
 * that can be used to retrieve the localized string.  If a messageId is not passed with the eventObject, or passed into
 * alternative functions that take parameters instead of an object, a messageId will be extracted from the eventId
 * (stripping off the 'nuskin.events' namespace, essentially).  This default functionality serves to simplify and
 * centralize the messaging.  But to override that, simply pass in the messageId value.
 *
 * Check the docs on the event publishing functions below for examples.
 *
 */
if (!nuskin.events) {
    /**
     * @namespace nuskin.events
     * @type {{global: {STRINGS_SERVICES_LOADED: string, CONFIGURATION_SET: string, DEVELEMENT_MODE: string}, errors: {CONFIGURATION: string, STRING_SERVICE: string, ATP: string, SAP: string, CART: string, PAYMENT: string, ADDRESS: string, PRODUCT_LOOKUP: string, PRODUCT_STATUS: string, SHIPPING_SERVICE: string, ORDER_SERVICE: string, UNEXPECTED_SYSTEM: string, INVALID_SEARCH_ADDRESS: string, NUMBER_STREET_ADDRESS: string}, authentication: {LOGIN: string, HIDE_LOGIN_POPUP: string, LOGOUT: string, LOGOUT_REQUESTED: string, LOGOUT_LEGACY: string, LOGIN_REQUESTED: string, SET_LOGIN_PARAMS: string, REQUEST_FOR_LOGIN: string, REQUEST_FOR_LOGIN_MODAL: string, INVALID_LOGIN: string, VALID: string, INVALID: string, INVALID_AUTH: string, INVALID_NEW_LOGIN_PAGE: string, USER_NOT_ALLOWED: string, INVALID_EMAIL: string, PING_COMPLETE: string, AGELOCME_LOGIN: string, ERROR_COOKIE_NAME: string, EID_LOGIN_PATH: string, INVALID_SHOPPING_COUNTRY: string, STATUS: string, ERROR_USER_NOT_FOUND: string, ERROR_LOGIN_FAILED: string, ERROR_SPONSOR_NOT_FOUND: string, ERROR_SHOPPING_NOT_ALLOWED: string, ERROR_LOGGED_OUT_FOR_INACTIVITY: string, ERROR_ACCOUNT_TERMINATED: string, ERROR_TOO_MANY_LOGIN_ATTEMPTS: string, ERROR_LOGOUT_MESSAGE: string}, account: {INIT_COMPLETE: string, VOLUME_ADDED: string, EMEASIGNUP_FLAG: string}, shop: {CART_UPDATED: string, ECOM3_CART_UPDATED: string, CART_RETURNED: string, ADD_TO_CART: string, REMOVE_FROM_CART: string, OUT_OF_STOCK: string, FAVORITES_LIST_LOADED: string, ADD_TO_FAVORITES_LIST: string, ITEM_REMOVED_FROM_FAVORITES: string, ADR_OPTIONS_READY: string, PRODUCT_SEARCH_CHANGED: string, PRODUCT_SEARCH_FAILED: string, PRODUCT_SELECTED: string, PRODUCT_SEARCH_CLEAR: string, DISPLAY_CART_CHANGED: string, AGELOC_ADR_INIT: string, CHECK_USER_SAME: string, CART_ERROR_MSG: string, CART_ITEM_UPDATED: string, CART_SCAN_CARDS_CHANGED: string, ADR_SHIP_DAYS_CHANGED: string, CHECKOUT_FOCUS: string, ADR_STATUS_CHANGE: string, ADD_FAVORITES_LOGIN: string, NEW_GOOGLE_MAPS_SEARCH: string, STORE_FRONT_ORDER_COMPLETE: string, OFFER_PRODUCT_REMOVED: string, OFFER_PRODUCTS_CHANGED: string, STOREFRONT_LOADED: string}, social: {SHOW_SHARE_URL: string, HIDE_SHARE_URL: string, URL_SHORTNER: string, SHORTEN_URL_ERROR: string}, video: {API_READY: string, PLAYER_READY: string, PLAYER_STATE_CHANGE: string, PLAYER_ERROR: string}, mec: {PITCH_LOADED: string, SHOW_TOAST_MSG: string, OFFER_LOADED: string, OFFER_TOTAL_UPDATED: string}, on: nuskin.events.subscribe, subscribe: nuskin.events.subscribe, emit: nuskin.events.publish, publish: nuskin.events.publish, publishError: nuskin.events.publishError, publishAjaxErrorEvent: nuskin.events.publishAjaxErrorEvent, publishAjaxError: nuskin.events.publishAjaxError, getErrorEventObject: nuskin.events.getErrorEventObject, getAjaxErrorEventObject: nuskin.events.getAjaxErrorEventObject, getMessageIdFromEventId: nuskin.events.getMessageIdFromEventId}}
     */
    (function () {
        // string constants used by default, to reference both the event type and the message string
        const ns = "nuskin.events.";
        const options = {
            type: 'localstorage', // (optional) enforce a type, oneOf['native', 'idb', 'localstorage', 'node']
            webWorkerSupport: false // (optional) set this to false if you know that your channel will never be used in a WebWorker (increases performance)
        };
        const eventChannel = new BroadcastChannel('eventChannel', options);


        // If you want to add extra metadata for your event add it here
        // You can specify to broadcast accross tabs
        // broadcast: 'reload'  will reload the page on all other tabs
        // broadcast: 'publish' will publish the event that was just published to other tabs
        const metadata = {
            LOGIN_COMPLETE: {
                key: "nuskin-account-loginComplete",
                broadcast: 'reload',
                publish: true
            },
            LOGOUT: {
                key: "nuskin-account-logout",
                broadcast: 'reload',
                publish: true
            },
            LOGOUT_LEGACY: {
                key: "nuskin-account-logout-legacy",
                broadcast: 'reload',
                publish: true
            },
            CART_UPDATED: {
                key: "nuskin-shop-cart",
                broadcast: null, // 'reload' is set after configuration is gotten. Look for CONFIGURATION_SET below.
                publish: true
            }
        };
        const events = {

            global: { // GLOBAL EVENTS
                BREADCRUMB: "breadcrumb",
                CONFIGURATION_SET: "CONFIGURATION_SET", // legacy configuration event, so I am keeping the string value the same, just in case.
                CS_CONFIG_SET: "cs-config-loaded", // constentStack configuration event.
                DEVELEMENT_MODE: "DEVELEMENT_MODE", // legacy configuration event, so I am keeping the string value the same, just in case.
                STRINGS_SERVICES_LOADED: ns + "stringsServicesLoaded",
                RUN_CONFIG_CHANGED: ns + "run-config-changed"
            },

            errors: { // GENERAL ERRORS
                ADDRESS: ns + "addressError",
                ATP: ns + "atpError",
                CART: ns + "cartError",
                CONFIGURATION: ns + "nuskinConfigurationError",
                INVALID_SEARCH_ADDRESS: "invalidSearchAddress",
                NUMBER_STREET_ADDRESS: "numberStreetAddress",
                ORDER_SERVICE: ns + "orderServiceError",
                PAYMENT: ns + "paymentError",
                PRODUCT_LOOKUP: ns + "productLookupError",
                PRODUCT_STATUS: ns + "productStatusError",
                SAP: ns + "sapError",
                SHIPPING_SERVICE: ns + "shippingServiceError",
                STRING_SERVICE: ns + "stringServiceError",
                UNEXPECTED_SYSTEM: ns + "unexpectedSystemError" // all unexpected errors go under this event - ajax, parsing,
            },

            authentication: {
                AGELOCME_LOGIN: "agelocme-login",
                EID_LOGIN_PATH: "/apps/global/services/eidlogin.html",
                ERROR_ACCOUNT_TERMINATED: "error-account-terminated",
                ERROR_COOKIE_NAME: "nuskin-staticauth-error",
                ERROR_LOGGED_OUT_FOR_INACTIVITY: "error-logged-out-for-inactivity",
                ERROR_LOGIN_FAILED: "error-login-failed",
                ERROR_LOGOUT_MESSAGE: "error-logout-message",
                ERROR_SHOPPING_NOT_ALLOWED: "error-shopping-not-allowed",
                ERROR_SPONSOR_NOT_FOUND: "error-sponsor-not-found",
                ERROR_TOO_MANY_LOGIN_ATTEMPTS: "error-too-many-login-attempts",
                ERROR_USER_NOT_FOUND: "error-user-not-found",
                FORGOT_PASSWORD_ERROR: "nuskin-forgot-password-error",
                FORGOT_PASSWORD_SENT: "nuskin-forgot-password-sent",
                HIDE_LOGIN_POPUP: "nuskin-account-hideLogin",
                INVALID: "nuskin-account-invalidAuthentication",   //event called when an error occurs during authentication
                INVALID_AUTH: "nuskin-account-invalidAuthentication", // This comes from static auth.
                INVALID_EMAIL: "invalid-email",
                INVALID_LOGIN: "invalid-login",
                INVALID_LOGIN_ATTEMPT: "invalid-login-attempt",
                INVALID_NEW_LOGIN_PAGE: "nuskin-account-invalidAuthentication-newLoginPage", // Use for new login page.
                INVALID_SHOPPING_COUNTRY: "invalid-shopping-country",
                LOGIN: "nuskin-account-login",  //event called when a user logs in
                LOGIN_COMPLETE: "nuskin-account-loginComplete",
                LOGIN_LOCKED: "nuskin-account-loginLocked",
                LOGIN_REQUESTED: "nuskin-account-loginRequested",
                LOGOUT: "nuskin-account-logout",  //event called when a user logs out
                LOGOUT_LEGACY: "nuskin-account-logout-legacy",
                LOGOUT_REQUESTED: "nuskin-account-logoutRequested",
                LTO_QUALIFICATIONS: "lto-qualifications",
                PING: "refresh-session",
                PING_COMPLETE: "nuskin-account-pingComplete",  //event called when a ping is complete
                PNP_CHECK: "pnp-check",
                REQUEST_FOR_LOGIN: "nuskin-account-requestForLogin",
                REQUEST_FOR_LOGIN_MODAL: "nuskin-account-requestForLoginModal",
                REQUEST_FOR_SIGNUP: "request-for-signup",
                RESET_PASSWORD_ERROR: "nuskin-password-error",
                RESET_PASSWORD_EXPIRED: "nuskin-password-expired",
                RESET_PASSWORD_SUCCESS: "nuskin-password-success",
                SET_LOGIN_PARAMS: "nuskin-account-setLoginParams",
                STATUS: "authentication-status",
                USER_NOT_ALLOWED: "nuskin-account-userNotAllowed",
                VALID: "nuskin-account-authentication"     //event called on valid authentication
            },

            account: {
                EMEASIGNUP_FLAG: "emeasignup-flag",
                INIT_COMPLETE: "nuskin-account-initializationComplete",  //event called when account initialization is complete
                INVITE_ASSOCIATE_EMAIL_FAILED: "invite-associate-email-failed",
                INVITE_ASSOCIATE_EMAIL_SENT: "invite-associate-email-sent",
                OPEN_FALLOUT_MODAL: "open-fallout-modal",
                PREFERRED_NAME_UPDATE: "preferred-name-updated",
                PROFILE_IMAGE_UPDATED: "profile-image-updated",
                VOLUME_ADDED: "nuskin-account-volumeAdded"   //event called when volume information loaded
            },

            profile: {
                ANALYTICS: "nuskin-profile"
            },

            shop: {
                ADD_FAVORITES_LOGIN: "add-favorites-login",
                ADD_SKU_PRODUCT_STATUS_QUEUE: "addSkuProductStatusQueue",
                ADD_TO_CART: "nuskin-shop-add-to-cart",
                ADD_TO_CART_NEW: "addToCart", //COM-2043: GA & GTM new cart and checkout events
                ADD_TO_FAVORITES_LIST: "add-to-favorites-list-message",
                ADR_OPTIONS_READY: ns + "adrOptionsReady",
                ADR_SHIP_DAYS_CHANGED: "adr-ship-days-changed",
                ADR_STATUS_CHANGE: "adr_status_change",
                AGELOC_ADR_INIT: "ageloc-adr-directive-init",
                CART_CHECKOUT: "checkout",
                CART_CHECKOUT_PURCHASE: "checkout-purchase",
                CART_ERROR_MSG: "add-cart-error-msg",
                CART_EVENT_PRICE_UPDATED: "cart-event-price-updated",
                CART_ITEM_UPDATED: "cart-item-updated",
                CART_RENDERED: "cart-rendered",
                CART_RETURNED: "nuskin-shop-cart-returned",
                CART_SCAN_CARDS_CHANGED: "cart-scan-cards-changed",
                CART_SYNCED_ON_LOAD: "cart-synced-on-load",
                CART_UPDATED: "nuskin-shop-cart",
                CHECK_USER_SAME: "check-for-same-user",
                CHECKOUT_FOCUS: "checkout-focus",
                CHECKOUT_ROUTE_CHANGING: "checkoutRouteChanging",
                CHECKOUT_USER_INFO: "checkoutUserInfo",
                DISPLAY_CART_CHANGED: "display-cart-changed",
                ECOM3_CART_UPDATED: "ecom3-nuskin-shop-cart",
                EMPTY_CART_CTA: "empty-cart-cta",
                FAVORITES_LIST_LOADED: "nuskin-favorites-list-loaded",
                GO_TO_PDP: "goToPDP",
                HIDE_PRODUCT_VARIANT_SELECTOR_MODAL: "hideProductVariantSelectorModal",
                INIT_CHECKOUT: "init-checkout",
                ITEM_REMOVED_FROM_FAVORITES: "item-removed-from-favorites-list",
                NEW_GOOGLE_MAPS_SEARCH: "newGoogleMapsSearch",
                NS_HERO_CLICKED: "nsHeroClicked",
                NS_JOIN_NUSKIN_FOOTER_CLICKED: "signupJoinNuSkinFooter",
                ORDER_PAYMENT_ERROR: "orderPaymentError",
                ORDER_SHIPPING_ERROR: "orderAddressError",
                OFFER_PRODUCT_REMOVED: "offerProductRemoved",
                OFFER_PRODUCTS_CHANGED: "offerVariantRemoved",
                ORDER_UPDATED: "orderUpdated",
                OUT_OF_STOCK: "nuskin-shop-out-of-stock",
                PERSONAL_OFFER_LOADED: "personalOfferLoaded",
                PRODUCT_IMPRESSION: "productImpression",  //COM-2043: GA & GTM new cart and checkout events
                PRODUCT_IMPRESSION_CLICK: "productClick",
                PRODUCT_IMPRESSION_DETAIL: "productDetailImpression",
                PRODUCT_SEARCH_CHANGED: "product-search-changed",
                PRODUCT_SEARCH_CLEAR: "product-search-clear",
                PRODUCT_SEARCH_FAILED: "product-search-failed",
                PRODUCT_SELECTED: "product-selected",
                PRODUCT_STATUS_RESULT: "productStatusResult",
                PROMOTION_CLICK:"promotionClick",
                PROMOTION_IMPRESSION:"promotionImpression",
                QUALIFICATION_SENT: "qualification-sent",
                QUALIFICATION_STATUS_CHANGED: "qualified-status-changed",
                REMOVE_FROM_CART: "nuskin-shop-remove-from-cart",
                REMOVE_FROM_CART_NEW: "removeFromCart",
                SEARCH_ADD_TO_CART: "addedToCartFromSearch", //For analytics
                SEARCH_EXECUTED: "searchExecuted", //Search has finished with results. Search results object is included
                SEARCH_NO_RESULTS: "searchNoResults",
                SEARCH_LOAD_MORE: "searchLoadMoreButtonUsed",
                SEARCH_POPULAR_PRODUCTS: "searchPopularProductSuggestionUsed", //Sent when a popular search suggestion is clicked
                SEARCH_RECENT: "searchRecentSearchesUsed", //Sent when a recent search item is clicked
                SEARCH_REQUESTED: "searchRequested", //A search has been initiated (clicked). Analytics event.
                SEARCH_SORT_DROPDOWN: "searchResultsSortByUsed",
                SEARCH_TYPEAHEAD: "searchTypeAheadUsed", //Sent when the type ahead search result is clicked
                SET_SHOPPING_CONTEXT: "setShoppingContext",
                SHOP_ERROR: "shopError",
                SHOW_ADD_TO_BAG: "show-add-to-bag",  // TODO Remove SHOW_ADD_TO_BAG once every market is on new universal pdp
                SHOW_EXCLUSIVE_OFFER_DETAILS: "showExclusiveOfferDetails",
                SHOW_PRODUCT_VARIANT_SELECTOR_MODAL: "showProductVariantSelectorModal",
                SIMULATE_COMPLETE: "simulate-complete",
                SPONSOR_SET: "sponsorSet",
                QUALIFICATION_SENT_EMAIL_FAILED: "qualificationSentEmailFailed",
                STORE_FRONT_ORDER_COMPLETE: "storeFrontOrderComplete",
                STOREFRONT_LOADED: "storefrontLoaded",
                TAG_HAS_SAVED_PAYMENT: "checkout.payment.has-saved-payment",
                TAG_METHOD_OF_PAYMENT: "selectMOPCheckoutPayment",
                HOME_MARKET_ONLY_RESTRICTED: "homeMarketOnlyRestricted",
                VARIANT_CHANGED: "variantChanged"
            },

            mobile: {
                NATIVE_INITIALIZING: "nativeInitializing"
            },

            recognition: {
                PROFILED_VIEWED: "profileViewed",
                RECOGNITION_SEARCH: "recognitionSearch"
            },

            signup: {
                BECOME_BRAND_AFFILIATE: "becomeBrandAffiliate",
                BUSINESS_SAVE_CONTINUE: "businesSaveAndContinue",
                CLICK: "clickSignup",
                CONVERT_TO_BUSINESS_ENTINTY: "convertToBusinessEntity",
                CREATE_USER: "createUser",
                INDIVIDUAL_SAVE_CONTINUE: "individualSaveAndContinue",
                SAVE: "saveSignup",
                SEARCH_DISTRIBUTOR: "searchDistributor",
                SEARCH_FINISHED: "searchFinished",
                SEARCH_SELECTED: "searchSelected",
                SIGNUP_TRACKING: "signup",
                SIGNUP_WEB_ACCOUNT_CREATE:  "signupWebAccountCreate",
                START_SIGNUP: "signupStart"
            },

            social: {
                HIDE_SHARE_URL: ns + "hideShareUrl",
                SHORTEN_URL_ERROR: ns + "shortenUrlError",
                SHOW_SHARE_URL: ns + "showShareUrl",
                URL_SHORTNER: ns + "UrlShortner"
            },

            video: {
                API_READY: ns + "videoApiReady",
                PLAYER_ERROR: ns + "videoPlayerError",
                PLAYER_READY: ns + "videoPlayerReady",
                PLAYER_STATE_CHANGE: ns + "videoPlayerStateChange"
            },

            mec: {
                OFFER_LOADED: "offerLoaded",
                OFFER_TOTAL_UPDATED: "offerTotalUpdated",
                PITCH_LOADED: "pitchLoaded",
                SHOW_TOAST_MSG: "showToastMsg"
            },

            salesevent: {
                ACTIVE_EVENTS: "active-events",     // fires when active events changes
                GO_TO_EVENT_PAGE: "go-to-event-page",
                IN_EVENT: "in-event",
                INIT_EVENT_TICKET: "init-event-ticket",
                INITIAL_ACTIVE_EVENTS: "initial-active-events", // fires once will all initial active events
                MARKET_CONFIG_EVENTS: "market-config-events",
                POLL_MARKET_CONFIG: "poll-market-config",
                POST_EVENT: "post-event",
                PRE_EVENT: "pre-event"
            },

            mySite: {
                EDIT_SECTION_CLICK: 'editSectionClick',
                PREVIEW_HEADER: 'previewHeader',
                SECTION_DATA_UPDATE: 'sectionDataUpdate',
                SITE_LOCALIZATION: 'siteLocalization',
                SUCCESSFULL_SETUP: 'successfullSetup',
                URL_EDIT_ALERTS: 'urlEditAlerts',
                URL_PREVIEW_SECTION: 'urlPreviewSection'
            },

            loyalty: {
                LOYALTY_BA_OPTIN_OPEN: 'loyaltyBaOptIn',
                LOYALTY_CART_MAX_LIMIT: 'loyaltyMaxLimit',
                LOYALTY_CART_REC_SPEND_CHANGE: 'loyaltyCartRecSpendChange',
                LOYALTY_CART_REWARDS: 'loyaltyCartRewards',
                LOYALTY_CART_TOAST_CLOSED: 'loyaltyCartToastClosed',
                LOYALTY_CART_USE_REWARDS: 'loyaltyUseRewards',
                LOYALTY_CART_WALLET_HOVER: 'loyaltyCartWalletHover',
                LOYALTY_CONVERT_OPEN: 'loyaltyConvertOpen',
                LOYALTY_CREATE_ACCT: 'loyaltyCreateAcct',
                LOYALTY_DATA_UPDATED: 'loyaltyDataUpdated',
                LOYALTY_JOIN_NOW: 'loyaltyJoinNow',
                LOYALTY_LEARN_INCREASE: 'loyaltyLearnIncrease',
                LOYALTY_LEARN: 'loyaltyLearn',
                LOYALTY_MODAL_CANCEL: 'loyaltyModalCancel',
                LOYALTY_MODAL_OUTSIDE_CANCEL: 'loyaltyModalOutsideCancel',
                LOYALTY_MODAL_XCANCEL: 'loyaltyModalXcancel',
                LOYALTY_OPT_IN: 'loyaltyOptIn',
                LOYALTY_OPT_IN_CLOSED: 'loyaltyOptInClosed',
                LOYALTY_OPTOUT_CANCEL: 'loyaltyOptoutCancel',
                LOYALTY_OPTOUT_CONFIRM: 'loyaltyOptoutConfirm',
                LOYALTY_OPTOUT_OUTSIDE_CANCEL: 'loyaltyOptoutOutsideCancel',
                LOYALTY_ORDER_INFO: 'loyaltyOrderInfo',
                LOYALTY_REWARDS_HEADER_BUTTON: 'loyaltyHdrButton',
                LOYALTY_REWARDS_PAGE_CHANGE: 'loyaltyTransactionPageChng',
                LOYALTY_SHOP: 'loyaltyShop',
                LOYALTY_SIGNUP_OPEN: 'loyaltySignupOpen',
                LOYALTY_SIGNUP_UPDATE: 'loyaltySignupUpdate',
                LOYALTY_START: 'loyaltyStart',
                LOYALTY_TERMS_AND_CONDITIONS: 'loyaltyTermsAndConditions',
                LOYALTY_PRIVACY_POLICY: 'loyaltyPrivacyPolicy',
                LOYALTY_TIER: 'loyaltyTier',
                LOYALTY_VIEW_DASH: 'loyaltyViewDash',
                UPDATE_LOYALTY_DATA: 'updateLoyaltyData'
            },

            subscriptions: {
                HOLD_CONFIRMED: 'holdConfirmed',
                HOLDS_DIALOG_OPENED: 'holdsDialogOpened',
                SUBSCRIPTION_LOYALTY_POINTS_UPDATED: 'subscriptionLoyaltyPointsUpdated'
            },

            header: {
                HIDE_SEARCHBAR: 'headerHideSearchbar',
                HIDE_NAVIGATION: 'headerHideNavigation',
                SET_DISTRIBUTOR_IMAGE: 'headerSetDistributorImage',
                HIDE_MENU: 'headerHideMenu',
                SET_LOGO_HREF: 'headerSetLogoHref',
                SET_HREF_OVERRIDE: 'headerSetHrefOverride',
                LANGUAGE_CHANGED: 'headerLanguageChanged'
            },

            // PUBLIC METHODS
            on: subscribe,
            subscribe: subscribe,
            unsubscribe: unsubscribe,
            emit: publish,
            publish: publish,
            publishError: publishError,
            publishAjaxErrorEvent: publishAjaxErrorEvent,
            publishAjaxError: publishAjaxError,
            getErrorEventObject: getErrorEventObject,
            getAjaxErrorEventObject: getAjaxErrorEventObject,
            getMessageIdFromEventId: getMessageIdFromEventId,
            setValue: setValue,
            getValue: getValue,

            /**
             * Pushes data into the dataLayer array to be used by a Tag Management System (TMS).
             * In essence, this triggers a tag.
             * Currently, we are using GTM. It is preferable to use this method rather than
             *  pushing into the dataLayer manually so that if the TMS is changed only the internals
             *  of this method need be changed.
             *
             * @param {string} event - The event name used to identify the tag being triggered.
             * @param {object} data - The data to be captured. Must be an object. The properties of this object will be
             *  spread into the data-layer, setting a data-layer-variable for each key/val.
             */
            tag: (event, data) => {
                if (!(data instanceof Object) || Array.isArray(data)) {
                    console.error(new TypeError(`When using events.tag, second argument must be an object but was passed ${data}.`))
                } else if (window && window.dataLayer && event !== "CONFIGURATION_SET") {
                    window.dataLayer.push({
                        event,
                        ...data
                    });
                }
            }
        };

        // jQuery pub/sub wrappers

        /**
         * Just a wrapper over the jQuery getValue (set up by nuskinjquery)
         * to centralize and possibly extend nuskin event handling in general.
         *
         * @param {string} eventId
         * @param {function} eventCallback
         * @param {function} doSubscribe true to listen for the event more than once
         * @memberof nuskin.events
         */
        function getValue(eventId, eventCallback, doSubscribe) {
            nsEvents.getValue(eventId, eventCallback, doSubscribe);
        }

        /**
         * Just a wrapper over the jQuery getValue (set up by nuskinjquery)
         * to centralize and possibly extend nuskin event handling in general.
         *
         * @param {string} eventId
         * @param {object} [eventObject]
         * @memberof nuskin.events
         */
        function setValue(eventId, eventObject) {
            nsEvents.setValue(eventId, eventObject);
        }

        /**
         * Just a wrapper over the jQuery unsubscribe to centralize and possibly extend nuskin event handling in general.
         *
         * @param {string} eventId
         * @memberof nuskin.events
         */
        function unsubscribe(eventId, fn) {
            nsEvents.unsubscribe(eventId, fn);
        }

        /**
         * Just a wrapper over the jQuery subscribe to centralize and possibly extend nuskin event handling in general.
         *
         * @param {string} eventId
         * @param {function} eventCallback
         * @memberof nuskin.events
         */
        function subscribe(eventId, eventCallback) {
            nsEvents.subscribe(eventId, eventCallback);
        }


        /**
         * Just a wrapper over the jQuery publish to centralize and possibly extend nuskin event handling in general.
         *
         * @param {string} eventId
         * @param {object} [eventObject]
         * @param {boolean} fromBroadcast  - The event was broadcasted from another tab.  Don't rebroadcast
         * @memberof nuskin.events
         */
        function publish(eventId, eventObject = null, fromBroadcast) {
            const mdata = getMetadata(eventId);
            if (!mdata || (mdata && mdata.publish)) {
                nsEvents.publish(eventId, eventObject);
            }
            // If we need to broadcast and it hasn't broadcasted yet
            if (mdata && mdata.broadcast && !fromBroadcast) {
                eventChannel.postMessage({
                    eventId: eventId,
                    eventObject: eventObject,
                    runConfigId: RunConfigUtil.getRunConfig().id});
            }
            // Publish events to react native apps that are using webviews
            if (window && window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify({name: 'web_event', payload: {eventId, eventObject}}));
            }
        }


        /**
         * Wrapper for centralizing and simplifying the general ERROR event publishing, via an object.
         *
         * @param {string}  eventId     - The string constant id for the event.  If a message id is not passed, the eventId is also used for extracting a message id used retrieving the localized message from common strings. If the Event id is passed, a standardized messageId will be derived therefrom.
         * @param {{messageId: string, messageString: string}}  [eventObject] - optional event object - will automatically extract the messageId from the event Id if none is passed.
         * @memberof nuskin.events
         * @private
         */
        function publishError(eventId, eventObject) {
            nsEvents.publish(eventId, events.getErrorEventObject(eventObject.messageId ? eventObject.messageId : eventId, eventObject.messageString));
        }


        /**
         * Wrapper for centralizing, standardizing, and simplifying the AJAX ERROR event publishing.
         *
         * @example <caption>Example usage</caption>
         * // Note that passing null for the messageId param makes it default to the value of 'eventId'
         * nuskin.events.publishAjaxErrorEvent(nuskin.events.ERROR_GENERIC, url, jqXHR, null, errorString);
         *
         * @param {string}  eventId - nuskin.events string constant used to reference both the error type and the message string.
         * @param {string}  url - URL of the JSON response
         * @param {object}  response - the general xhr response object, containing both a 'status' and a 'statusText' parameter.
         * @param {string}  [messageId] - reference for the string ID used in CQ common strings for use with the StringServices object. Defaults to the Event string minus the prefix namespace, "nuskin.events."
         * @param {string}  [messageString] - optional message string if an messageId lookup isn't ideal.
         * @memberof nuskin.events
         *
         */
        function publishAjaxErrorEvent(eventId, url, response, messageId, messageString) {
            nsEvents.publish(eventId, events.getAjaxErrorEventObject(messageId ? messageId : eventId, url, response, messageString));
        }


        /**
         * Wrapper for centralizing and simplifying the ajax error event publishing, via an object.
         *
         * @example <caption>Example usage</caption>
         * var eventObj = {
         *     messageId:"myCustomMessageId",
         *     url:"someurl.com",
         *     response: httpResponse
         * }
         * nuskin.events.publishAjaxError(evtId, eventObj);
         *
         * @param {string}  eventId     - The string constant id for the event.  If a messageId is not sent, the eventId is also used for retrieving the localized message from common strings.
         * @param {{url: string, response: *, messageId: string, messageString: string}}  eventObject
         * @memberof nuskin.events
         */
        function publishAjaxError(eventId, eventObject) {
            nsEvents.publish(eventId, events.getAjaxErrorEventObject(eventObject.messageId ? eventObject.messageId : eventId, eventObject.url, eventObject.response, eventObject.messageString));
        }


        /**
         * Factory function for standardizing and centralizing the creation of the GENERIC error event objects
         *
         * @param {string}  id - The id used for retrieving the localized message from common strings. If the Event id is passed, a standardized messageId will be derived therefrom.
         * @param {string}  [messageString]
         *
         * @returns {{messageId: string, messageString: string}} - messageId is for use with the localized common strings
         * @memberof nuskin.events
         */
        function getErrorEventObject(id, messageString) {
            return {
                messageId: events.getMessageIdFromEventId(id),
                messageString: messageString
            };
        }


        /**
         * Factory function for standardizing and centralizing the creation of the AJAX error event objects
         *
         * @param {string}  id - The id used for retrieving the localized message from common strings. If the Event id is passed, a standardized messageId will be derived therefrom.
         * @param {string}  url
         * @param {Object}  response - the xhr response
         * @param {string}  [messageString]
         *
         * @returns {{url: string, response: *, messageId: string, messageString: string}}
         * @memberof nuskin.events
         */
        function getAjaxErrorEventObject(id, url, response, messageString) {
            return _.extend(getErrorEventObject(id, messageString), {
                url: url,
                response: response
            });
        }


        /**
         * Standardizes and centralizes the default practice of repurposing or coupling event and message ids.
         *
         * @param {string}      id
         *
         * @returns {string}
         * @memberof nuskin.events
         */
        function getMessageIdFromEventId(id) {
            if (id == undefined) {
                throw new Error("ERROR in nuskin.events.js.getMessageIdFromEventId():: id is undefined.");
            }
            let index = id.indexOf(ns);
            if (index > -1) {
                id = id.substring(id.indexOf(ns) + ns.length);
            }
            return id;
        }


        /**
         * Gets the metadata for the given key
         *
         * @param key
         * @returns {Object} metadata
         */
        const getMetadata = (key) => {
            let mdata = null;
            const item = Object.keys(metadata).find((k) => metadata[k].key === key);
            if (item) {
                mdata = metadata[item];
            }
            return mdata;
        }

        // Listen for BroadcastChannel events and reload the page or publish the event on the tab
        eventChannel.onmessage = message => {
            if (RunConfigUtil.getRunConfig().id !== message.runConfigId) {
                getMetadata(message.eventId).broadcast === 'publish' ?
                    publish(message.eventId, message.eventObject, true) :
                    window.location.reload();
            }
        }

        nuskin.events = events;
    })();
}

export default nuskin.events;
